// Abas "Conciliação" (planilha × ERP, contrato a contrato) e "Metas e histórico"
// (metas anual/mensal/por setor e os totais de jan–set/2026 vindos do Resumo).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { App as AntApp, Button, Card, Col, Flex, Input, InputNumber, Row, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { usePerms } from '../../hooks/usePerms';
import * as api from './api';
import type { Conciliacao, LinhaConciliacao } from './api';
import { brl, dataBr, lerLinhasAbaMensal, lerResumo, MESES, nomeDe, ORDEM_SETORES, SETOR_CURTO, SETOR_NOMES } from './regras';

const { Text, Paragraph } = Typography;
const PERM_EDITAR = 'operacional.entregas_mensais.editar';

const textoSetores = (s: Record<string, number> | null | undefined) =>
  Object.entries(s || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${SETOR_CURTO[k] || k} ${Math.round(v)}%`).join(' · ') || '—';

export function AbaConciliacao() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode(PERM_EDITAR);
  const [texto, setTexto] = useState('');
  const [aba, setAba] = useState('');
  const [colar, setColar] = useState(false);
  const [r, setR] = useState<Conciliacao | null>(null);
  const [dados, setDados] = useState<api.AbaConciliacaoDados | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [soDivergencia, setSoDivergencia] = useState(true);
  const lidas = useMemo(() => lerLinhasAbaMensal(texto), [texto]);

  // A aba abre com a última foto gravada da planilha, conciliada contra o ERP de agora.
  const abrir = useCallback(async () => {
    setCarregando(true);
    try {
      const d = await api.obterConciliacao();
      setDados(d);
      setR(d.resumo && d.linhas ? { linhas: d.linhas, resumo: d.resumo } : null);
      if (!d.foto) setColar(true);
    } catch (e) { message.error(`Não carregou a conciliação: ${api.erroDe(e)}`); }
    finally { setCarregando(false); }
  }, [message]);
  useEffect(() => { abrir(); }, [abrir]);

  async function gravar() {
    setCarregando(true);
    try {
      if (podeEditar) { await api.gravarConciliacao(aba.trim() || 'colada', lidas); setTexto(''); setColar(false); await abrir(); }
      else setR(await api.conciliar(lidas));
    } catch (e) { message.error(api.erroDe(e)); }
    finally { setCarregando(false); }
  }

  async function adotar(linhas: LinhaConciliacao[]) {
    let ok = 0;
    for (const l of linhas) {
      if (!l.contrato_id) continue;
      try {
        if (!l.erp?.itens) await api.puxarParcelas(l.contrato_id);
        await api.salvarAbertura(l.contrato_id, l.planilha.entregue_acumulado, `Acumulado da planilha (${l.rotulos.join(' + ')})`);
        ok++;
      } catch (e) { message.error(`${l.codigo}: ${api.erroDe(e)}`); }
    }
    message.success(`${ok} contrato(s) adotado(s) no controle.`);
    await abrir();
  }

  async function cancelarItem(id: string, versao: number, descricao: string) {
    modal.confirm({
      title: 'Cancelar este entregável?', content: `“${descricao}” sai da previsão e do saldo em aberto; o registro fica.`,
      okText: 'Cancelar entregável', okButtonProps: { danger: true }, cancelText: 'Voltar',
      onOk: async () => {
        try { await api.salvarItem({ id, versao, status: 'cancelado' }); message.success('Entregável cancelado.'); await abrir(); }
        catch (e) { message.error(api.erroDe(e)); }
      },
    });
  }

  const pend = dados?.pendencias;
  const linhasTabela = (r?.linhas || []).filter(l => !soDivergencia || l.problemas.length);

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Flex gap={12} align="center" wrap>
          {dados?.foto
            ? <Text>Planilha de referência: <b>{dados.foto.aba || 'colada'}</b> · {dados.foto.qtd_linhas} linhas · gravada por {nomeDe(dados.foto.criado_por)} em {dataBr(dados.foto.criado_em)}. Conciliada contra o ERP de agora.</Text>
            : <Text type="secondary">Nenhuma planilha gravada ainda. Cole as linhas de uma aba mensal para a conciliação ficar salva aqui.</Text>}
          <div style={{ flex: 1 }} />
          <Button onClick={() => setColar(c => !c)}>{colar ? 'Fechar colagem' : 'Atualizar com a planilha'}</Button>
          <Button onClick={abrir} loading={carregando}>Recarregar</Button>
        </Flex>
        {colar && (
          <div style={{ marginTop: 12 }}>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              Copie da planilha as linhas de uma aba mensal (ex.: OUTUBRO_26), da coluna <b>Projeto</b> até <b>Equipe</b>, e cole abaixo.
              O sistema casa pelo número do contrato (P26050 = HY26050), soma as linhas do mesmo contrato partidas por setor e compara
              valor total, parcelas, acumulado e divisão por setor com o comercial/financeiro. Vale a divisão do ERP.
            </Paragraph>
            <Flex gap={8} wrap style={{ marginBottom: 8 }}>
              <Input style={{ width: 220 }} placeholder="Nome da aba (ex.: OUTUBRO_26)" value={aba} onChange={e => setAba(e.target.value)} maxLength={80} />
            </Flex>
            <Input.TextArea rows={6} value={texto} onChange={e => setTexto(e.target.value)} placeholder="Cole aqui (Ctrl+V) as linhas copiadas da planilha" style={{ fontFamily: 'monospace', fontSize: 12 }} />
            <Button type="primary" style={{ marginTop: 8 }} disabled={!lidas.length} loading={carregando} onClick={gravar}>
              {podeEditar ? 'Gravar e conciliar' : 'Conciliar'} {lidas.length ? `${lidas.length} linhas` : ''}
            </Button>
          </div>
        )}
      </Card>

      {pend && (pend.a_conciliar.length > 0 || pend.diferencas.length > 0) && (
        <Card size="small" style={{ marginBottom: 12 }} title={`Pendências do controle (${pend.a_conciliar.length + pend.diferencas.length})`}>
          {pend.a_conciliar.length > 0 && (
            <>
              <Text strong>Entregáveis criados na importação para cobrir a planilha (“a conciliar”)</Text>
              <Table<api.PendenciaItem> rowKey="item_id" size="small" pagination={false} style={{ margin: '6px 0 16px' }} dataSource={pend.a_conciliar}
                columns={[
                  { title: 'Contrato', render: (_, i) => <span><Text strong>{i.numero}</Text> {i.titulo}</span> },
                  { title: 'Entregável', dataIndex: 'descricao' },
                  { title: 'Saldo', dataIndex: 'saldo', align: 'right', render: v => brl(v) },
                  { title: 'Por quê', dataIndex: 'observacao', render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v || '—'}</Text> },
                  { title: '', render: (_, i) => podeEditar && <Button size="small" danger onClick={() => cancelarItem(i.item_id, i.versao, i.descricao)}>Cancelar</Button> },
                ]} />
            </>
          )}
          {pend.diferencas.length > 0 && (
            <>
              <Text strong>Contratos em que os entregáveis em aberto não fecham com o saldo</Text>
              <Table<api.PendenciaContrato> rowKey="contrato_id" size="small" pagination={false} style={{ marginTop: 6 }} dataSource={pend.diferencas}
                expandable={{
                  expandedRowRender: d => (
                    <Table rowKey="id" size="small" pagination={false} dataSource={d.itens}
                      columns={[
                        { title: 'Entregável em aberto', dataIndex: 'descricao' },
                        { title: 'Parcela', dataIndex: 'parcela_status', render: v => (v ? <Tag>{v}</Tag> : <Tag>sem parcela</Tag>) },
                        { title: 'Saldo', dataIndex: 'saldo', align: 'right', render: v => brl(v) },
                        { title: '', render: (_, i) => podeEditar && <Button size="small" onClick={() => cancelarItem(i.id, i.versao, i.descricao)}>Cancelar</Button> },
                      ]} />
                  ),
                }}
                columns={[
                  { title: 'Contrato', render: (_, d) => <span><Text strong>{d.numero}</Text> {d.titulo}<br /><Text type="secondary" style={{ fontSize: 12 }}>{nomeDe(d.responsavel)}</Text></span> },
                  { title: 'Valor (ERP)', dataIndex: 'valor_total', align: 'right', render: v => brl(v) },
                  { title: 'Acumulado', dataIndex: 'acumulado', align: 'right', render: v => brl(v) },
                  { title: 'Saldo a entregar', dataIndex: 'saldo_a_entregar', align: 'right', render: v => brl(v) },
                  { title: 'Entregáveis em aberto', dataIndex: 'itens_em_aberto', align: 'right', render: v => brl(v) },
                  { title: 'Diferença', dataIndex: 'diferenca', align: 'right', render: v => <Text type="danger">{brl(v)}</Text> },
                  { title: 'Leitura', dataIndex: 'leitura', render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
                ]} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Para resolver: cancele o entregável que já foi entregue antes do controle, ajuste o acumulado de abertura (aba Mês → Ações) ou corrija parcelas/aditivos no contrato.
              </Text>
            </>
          )}
        </Card>
      )}

      <Flex gap={12} align="center" wrap style={{ margin: '8px 0 12px' }}>
        {r && <Text>{r.resumo.contratos} contratos · <Text type="danger">{r.resumo.com_problema} com divergência</Text> · {r.resumo.sem_contrato} sem contrato no ERP</Text>}
        {r && <Flex gap={6} align="center"><Switch size="small" checked={soDivergencia} onChange={setSoDivergencia} /><Text>Só com divergência</Text></Flex>}
        <div style={{ flex: 1 }} />
        {r && podeEditar && (
          <Button onClick={() => modal.confirm({
            title: 'Adotar todos os contratos encontrados?',
            content: 'Para cada contrato achado no ERP: cria os entregáveis a partir das parcelas (se ainda não houver) e grava o “Entregue acumulado” da planilha como acumulado de abertura. Nada muda no comercial nem no financeiro.',
            okText: 'Adotar', onOk: () => adotar(r.linhas.filter(l => l.contrato_id)),
          })}>Adotar todos no controle</Button>
        )}
      </Flex>
      {r && (
        <Table<LinhaConciliacao> rowKey={l => l.codigo || l.rotulos[0]} size="small" dataSource={linhasTabela} pagination={false} scroll={{ x: 1400 }}
          columns={[
            { title: 'Planilha', render: (_, l) => <span>{l.rotulos.map(x => <div key={x}>{x}</div>)}</span>, width: 300 },
            { title: 'ERP', render: (_, l) => l.numero_erp ? <span><Text strong>{l.numero_erp}</Text> {l.titulo_erp}</span> : <Tag color="red">não achado</Tag>, width: 240 },
            {
              title: 'Valor total (planilha / ERP)', align: 'right', render: (_, l) => (
                <span>{brl(l.planilha.valor_total)}<br /><Text type={l.diferenca_valor_total ? 'danger' : 'secondary'}>{l.erp ? brl(l.erp.valor_total) : '—'}</Text></span>
              ),
            },
            { title: 'Parcelas · faturado (ERP)', align: 'right', render: (_, l) => l.erp ? <span>{brl(l.erp.parcelas_total)}<br /><Text type="secondary">{brl(l.erp.faturado)}</Text></span> : '—' },
            {
              title: 'Acumulado (planilha / sistema)', align: 'right', render: (_, l) => (
                <span>{brl(l.planilha.entregue_acumulado)}<br /><Text type="secondary">{l.erp ? (l.erp.abertura == null && !l.erp.entregue_sistema ? 'não adotado' : brl((l.erp.abertura || 0) + l.erp.entregue_sistema)) : '—'}</Text></span>
              ),
            },
            { title: 'Setores (planilha / ERP)', render: (_, l) => <span>{textoSetores(l.planilha.setores)}<br /><Text type="secondary">{textoSetores(l.erp?.setores)}</Text></span>, width: 260 },
            { title: 'Divergências', render: (_, l) => l.problemas.length ? l.problemas.map(p => <Tag key={p} color="orange" style={{ whiteSpace: 'normal', marginBottom: 2 }}>{p}</Tag>) : <Tag color="green">ok</Tag>, width: 300 },
            { title: '', render: (_, l) => podeEditar && l.contrato_id && <Button size="small" onClick={() => adotar([l])}>Adotar</Button> },
          ]} />
      )}
    </div>
  );
}

export function AbaMetas() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode(PERM_EDITAR);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [painel, setPainel] = useState<api.Painel | null>(null);
  const [anual, setAnual] = useState<number | null>(null);
  const [superAnual, setSuperAnual] = useState<number | null>(null);
  const [mensal, setMensal] = useState<(number | null)[]>(Array(12).fill(null));
  const [porSetor, setPorSetor] = useState<Record<string, number | null>>({});
  const [textoResumo, setTextoResumo] = useState('');
  // Só meses antes do controle no sistema (o Resumo traz o ano inteiro).
  const historicoLido = useMemo(() => lerResumo(textoResumo, ano)
    .filter(h => !painel || h.competencia < painel.inicio_controle), [textoResumo, ano, painel]);

  const carregar = async (a: number) => {
    try {
      const p = await api.obterPainel(a);
      setPainel(p);
      const geral = p.metas.find(m => m.mes == null && !m.setor);
      setAnual(geral?.valor ?? null); setSuperAnual(geral?.supermeta ?? null);
      setMensal(Array.from({ length: 12 }, (_, i) => p.metas.find(m => m.mes === i + 1 && !m.setor)?.valor ?? null));
      setPorSetor(Object.fromEntries(ORDEM_SETORES.filter(s => s !== 'sem_setor').map(s => [s, p.metas.find(m => m.mes == null && m.setor === s)?.valor ?? null])));
    } catch (e) { message.error(api.erroDe(e)); }
  };
  useEffect(() => { carregar(ano); }, [ano]); // eslint-disable-line react-hooks/exhaustive-deps

  async function salvar() {
    const metas: { mes: number | null; setor: string | null; valor: number; supermeta?: number | null }[] = [];
    if (anual) metas.push({ mes: null, setor: null, valor: anual, supermeta: superAnual });
    mensal.forEach((v, i) => { if (v) metas.push({ mes: i + 1, setor: null, valor: v }); });
    Object.entries(porSetor).forEach(([s, v]) => { if (v) metas.push({ mes: null, setor: s, valor: v }); });
    try { await api.salvarMetas(ano, metas); message.success('Metas salvas.'); await carregar(ano); }
    catch (e) { message.error(api.erroDe(e)); }
  }
  async function importarHistorico() {
    try {
      const r = await api.salvarHistorico(historicoLido);
      message.success(`${r.gravadas} valores do histórico gravados.`);
      setTextoResumo(''); await carregar(ano);
    } catch (e) { message.error(api.erroDe(e)); }
  }

  const hist = painel?.historico || [];
  const valorHist = (mes: number, tipo: string, setor = 'total') =>
    hist.find(h => h.competencia.startsWith(`${ano}-${String(mes).padStart(2, '0')}`) && h.tipo === tipo && h.setor === setor)?.valor ?? null;
  const mesesHistorico = painel ? Array.from({ length: 12 }, (_, i) => i + 1).filter(m => `${ano}-${String(m).padStart(2, '0')}-01` < painel.inicio_controle) : [];

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={10}>
        <Card size="small" title={<Flex gap={8} align="center">Metas de entrega <Select size="small" style={{ width: 100 }} value={ano} onChange={setAno} showSearch optionFilterProp="label" options={[ano - 1, ano, ano + 1].map(a => ({ value: a, label: String(a) }))} /></Flex>}
          extra={podeEditar && <Button type="primary" size="small" onClick={salvar}>Salvar metas</Button>}>
          <Flex gap={12} wrap>
            <div><Text>Meta anual</Text><br /><InputNumber style={{ width: 170 }} min={0} decimalSeparator="," disabled={!podeEditar} value={anual ?? undefined} onChange={v => setAnual(v)} /></div>
            <div><Text>Supermeta anual</Text><br /><InputNumber style={{ width: 170 }} min={0} decimalSeparator="," disabled={!podeEditar} value={superAnual ?? undefined} onChange={v => setSuperAnual(v)} /></div>
          </Flex>
          <Text type="secondary" style={{ display: 'block', margin: '12px 0 4px' }}>Meta mensal (vazio = anual ÷ 12{anual ? ` = ${brl(anual / 12)}` : ''})</Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
            {mensal.map((v, i) => (
              <div key={i}><Text style={{ fontSize: 12 }}>{MESES[i]}</Text><br />
                <InputNumber size="small" style={{ width: '100%' }} min={0} decimalSeparator="," disabled={!podeEditar} value={v ?? undefined}
                  onChange={x => setMensal(m => m.map((y, j) => (j === i ? x : y)))} /></div>
            ))}
          </div>
          <Text type="secondary" style={{ display: 'block', margin: '12px 0 4px' }}>Meta anual por setor (opcional)</Text>
          <Flex vertical gap={6}>
            {Object.keys(porSetor).map(s => (
              <Flex key={s} justify="space-between" align="center" gap={8}>
                <Text>{SETOR_NOMES[s]}</Text>
                <InputNumber size="small" style={{ width: 170 }} min={0} decimalSeparator="," disabled={!podeEditar} value={porSetor[s] ?? undefined}
                  onChange={x => setPorSetor(p => ({ ...p, [s]: x }))} />
              </Flex>
            ))}
          </Flex>
        </Card>
      </Col>
      <Col xs={24} xl={14}>
        <Card size="small" title="Histórico da planilha (meses antes do controle no sistema)">
          <Table size="small" rowKey="mes" pagination={false} scroll={{ x: 900 }}
            dataSource={mesesHistorico.map(m => ({ mes: m }))}
            columns={[
              { title: 'Mês', dataIndex: 'mes', render: m => MESES[m - 1] },
              { title: 'Baseline', render: (_, x) => brl(valorHist(x.mes, 'baseline')), align: 'right' },
              { title: 'Ajuste', render: (_, x) => brl(valorHist(x.mes, 'ajuste')), align: 'right' },
              { title: 'Realizado', render: (_, x) => brl(valorHist(x.mes, 'realizado')), align: 'right' },
              ...ORDEM_SETORES.filter(s => s !== 'sem_setor').map(s => ({
                title: SETOR_CURTO[s], key: s, align: 'right' as const, render: (_: any, x: { mes: number }) => brl(valorHist(x.mes, 'realizado', s)),
              })),
            ]} />
          {podeEditar && (
            <div style={{ marginTop: 12 }}>
              <Tooltip title="Na aba Resumo, selecione as linhas dos meses (coluna Mês/Trimestre até o último setor) e copie. Linhas de trimestre e total são ignoradas.">
                <Text>Colar do Resumo da planilha ({ano})</Text>
              </Tooltip>
              <Input.TextArea rows={4} value={textoResumo} onChange={e => setTextoResumo(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 12 }}
                placeholder="janeiro/2026	R$ 407.952,00	R$ 460.707,00	R$ 375.000,00	R$ 374.917,48	…" />
              <Flex gap={8} align="center" style={{ marginTop: 8 }}>
                <Button disabled={!historicoLido.length} onClick={importarHistorico}>Gravar histórico ({new Set(historicoLido.map(h => h.competencia)).size} meses)</Button>
                <Text type="secondary">Só meses anteriores a {painel ? MESES[Number(painel.inicio_controle.slice(5, 7)) - 1] : '—'}; o servidor recusa o resto.</Text>
              </Flex>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
}
